import Link from 'next/link'

import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { nextSectionRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type NextSectionProps = {
  overline: string
  title: string
  link: WPLink
  theme?: 'light' | 'dark'
  direction?: 'next' | 'prev'
}

const NextSection = ({
  overline,
  title,
  link,
  theme,
  direction
}: NextSectionProps) => {
  return (
    <Motion animation={nextSectionRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} className="mt-spacing-xl lg:mt-0">
          <Grid>
            <div
              className={cn([
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-8 lg:row-start-3 lg:w-col-span-4',
                'grid items-center justify-center'
              ])}
            >
              <Link
                href={link.url}
                scroll={false}
                className="group flex flex-col items-center"
              >
                <span className="flex">
                  <Typography
                    variant="h6"
                    className="text-text-handwritten-light"
                    data-motion-overline
                  >
                    {overline}
                  </Typography>
                </span>
                <span className="mt-spacing-xs flex [&_.char-wrap]:pb-[0.05em]">
                  <Typography variant="h2" data-motion-title>
                    {title}
                  </Typography>
                </span>
                <span
                  className="mt-spacing-xs flex h-[8rem] w-[8rem] overflow-hidden rounded-full"
                  data-motion-btn-wrapper
                >
                  <span className="flex h-full w-full" data-motion-btn-content>
                    <span
                      className={cn(
                        'flex h-full w-full items-center justify-center rounded-full',
                        'group transition-transform duration-500 ease-out-cubic will-change-transform group-hover:scale-90',
                        {
                          'bg-surface-primary-light': theme === 'light',
                          'bg-surface-primary-dark': theme === 'dark'
                        }
                      )}
                    >
                      <span
                        className={cn([
                          'relative block h-[2.5rem] w-[2rem] overflow-hidden',
                          'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:scale-110'
                        ])}
                      >
                        <SVGIcon
                          symbol={
                            direction === 'next' ? 'arrow-down' : 'arrow-up'
                          }
                          className={cn([
                            'h-[2.5rem] w-[2rem] fill-surface-primay-invert-light',
                            'transition-transform duration-500 ease-out-cubic will-change-transform',
                            {
                              '-translate-y-full group-hover:translate-y-0':
                                direction === 'next',
                              'translate-y-0 group-hover:-translate-y-full':
                                direction === 'prev'
                            }
                          ])}
                        />
                        <SVGIcon
                          symbol={
                            direction === 'next' ? 'arrow-down' : 'arrow-up'
                          }
                          className={cn([
                            'h-[2.5rem] w-[2rem] fill-surface-primay-invert-light',
                            'transition-transform duration-500 ease-out-cubic will-change-transform',
                            {
                              '-translate-y-full group-hover:translate-y-0':
                                direction === 'next',
                              'translate-y-0 group-hover:-translate-y-full':
                                direction === 'prev'
                            }
                          ])}
                        />
                      </span>
                    </span>
                  </span>
                </span>
              </Link>
            </div>
          </Grid>
        </Section>
      )}
    </Motion>
  )
}

export default NextSection
